import type { AutoTrackingReservation } from '@tryiris-ai/common.protojs/studio/v1/studio';
import type { ByteId } from '#~/api/index.js';
import { toHexFromId } from '#~/api/index.js';

/**
 * Generates the subject for auto tracking annotations
 *
 * @param agentId - The id of the agent.
 * @param cameraId - The id of the camera.
 * @param workerId - The id of the worker.
 *
 * @returns The subject for the auto tracking annotations.
 */
export const annotationSubjectForAgentCameraAndWorker = (
  agentId: ByteId,
  cameraId: ByteId,
  workerId: ByteId
): string =>
  `agent.${toHexFromId(agentId)!}.camera.${toHexFromId(cameraId)!}.worker.${toHexFromId(workerId)!}.annotation`;

export const embeddingBeaconSubjectForAgentCameraAndWorker = (
  agentId: ByteId,
  cameraId: ByteId,
  workerId: ByteId
): string =>
  `${annotationSubjectForAgentCameraAndWorker(agentId, cameraId, workerId)}.embedding_beacon`;

/**
 * Generates the subject for auto tracking annotations
 *
 * @param agentId - The id of the agent.
 * @param cameraId - The id of the camera.
 * @param workerId - The id of the worker.
 *
 * @returns The subject for the auto tracking annotations.
 */
export const pingSubjectForWorker = (agentId: ByteId, workerId: ByteId): string =>
  `agent.${toHexFromId(agentId)!}.worker.${toHexFromId(workerId)!}.ping`;

export const pingSubjectForReservation = (agentId: ByteId, reservationId: ByteId): string =>
  `agent.${toHexFromId(agentId)!}.reservation.${toHexFromId(reservationId)!}.ping`;

/**
 * Check if the specified reservation is valid.
 *
 * @param reservation - The reservation to check.
 * @returns Whether the reservation is valid.
 */
export const checkReservationValidity = (reservation: AutoTrackingReservation): boolean => {
  if (!reservation) {
    return false;
  }

  const { cameraId } = reservation;

  return !!(cameraId !== undefined);
};

/**
 * Gets a unique hex string for a reservation using the camera id.
 *
 * @param cameraId - The camera id to get the key for.
 * @returns The unique key for the reservation.
 */
export const reservationKeyForCameraId = (sessionId: string, cameraId: ByteId): string =>
  `${sessionId}${toHexFromId(cameraId)}`.toLowerCase();
